import type { ReactNode } from 'react';

/**
 * 경량 마크다운(의존성 0) — 에이전트 답변용: 제목(#~###)·목록·표·굵게·인라인 코드·구분선.
 * 전부 텍스트 렌더(HTML 주입 없음). 모르는 문법은 문단으로 강등 — 깨지지 않는 게 우선.
 */

function inline(text: string, keyBase: string): ReactNode[] {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return (
        <b key={`${keyBase}-${i}`} className="font-semibold text-ink">
          {p.slice(2, -2)}
        </b>
      );
    }
    if (p.startsWith('`') && p.endsWith('`')) {
      return (
        <code key={`${keyBase}-${i}`} className="rounded bg-bg-2 px-1 font-mono text-[0.92em] text-star/90">
          {p.slice(1, -1)}
        </code>
      );
    }
    return p;
  });
}

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'h'; level: number; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'table'; rows: string[][] }
  | { kind: 'hr' };

function parse(src: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) blocks.push({ kind: 'p', lines: para });
    para = [];
  };
  const lines = src.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const t = line.trim();
    if (!t) {
      flush();
      continue;
    }
    const h = t.match(/^(#{1,3})\s+(.*)$/);
    if (h) {
      flush();
      blocks.push({ kind: 'h', level: h[1].length, text: h[2] });
      continue;
    }
    if (/^(-{3,}|—{3,})$/.test(t)) {
      flush();
      blocks.push({ kind: 'hr' });
      continue;
    }
    if (t.startsWith('|') && t.endsWith('|')) {
      flush();
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        const cells = lines[i]
          .trim()
          .replace(/^\||\|$/g, '')
          .split('|')
          .map((c) => c.trim());
        if (!cells.every((c) => /^:?-{2,}:?$/.test(c))) rows.push(cells); // 구분행 스킵
        i++;
      }
      i--;
      blocks.push({ kind: 'table', rows });
      continue;
    }
    if (/^[-*]\s+/.test(t)) {
      flush();
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ''));
        i++;
      }
      i--;
      blocks.push({ kind: 'ul', items });
      continue;
    }
    if (/^\d+\.\s+/.test(t)) {
      flush();
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
        i++;
      }
      i--;
      blocks.push({ kind: 'ol', items });
      continue;
    }
    para.push(t);
  }
  flush();
  return blocks;
}

export function Markdown({ text, className }: { text: string; className?: string }) {
  const blocks = parse(text);
  return (
    <div className={className}>
      {blocks.map((b, i) => {
        switch (b.kind) {
          case 'h':
            return (
              <p key={i} className={`mb-1.5 mt-3 first:mt-0 font-semibold text-ink ${b.level === 1 ? 'text-[16.5px]' : 'text-[14.5px]'}`}>
                {inline(b.text, `h${i}`)}
              </p>
            );
          case 'hr':
            return <div key={i} className="my-2 h-px bg-line" />;
          case 'ul':
            return (
              <ul key={i} className="mb-2 space-y-0.5 pl-1">
                {b.items.map((it, j) => (
                  <li key={j} className="flex gap-1.5 text-[13.5px] leading-6 text-dim">
                    <span className="text-fainter">·</span>
                    <span className="min-w-0">{inline(it, `u${i}-${j}`)}</span>
                  </li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={i} className="mb-2 space-y-0.5 pl-1">
                {b.items.map((it, j) => (
                  <li key={j} className="flex gap-1.5 text-[13.5px] leading-6 text-dim">
                    <span className="font-mono text-[11px] text-fainter">{j + 1}.</span>
                    <span className="min-w-0">{inline(it, `o${i}-${j}`)}</span>
                  </li>
                ))}
              </ol>
            );
          case 'table':
            return (
              <div key={i} className="mb-2 overflow-x-auto rounded border border-line">
                <table className="w-full text-[12.5px] leading-6">
                  <tbody>
                    {b.rows.map((row, r) => (
                      <tr key={r} className={r === 0 ? 'bg-bg-2/70 text-ink' : 'border-t border-line text-dim'}>
                        {row.map((cell, c) => (
                          <td key={c} className="px-2 py-1 align-top">
                            {inline(cell, `t${i}-${r}-${c}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return (
              <p key={i} className="mb-2 whitespace-pre-wrap text-[13.5px] leading-7 text-dim last:mb-0">
                {b.lines.map((ln, j) => (
                  <span key={j}>
                    {j > 0 && '\n'}
                    {inline(ln, `p${i}-${j}`)}
                  </span>
                ))}
              </p>
            );
        }
      })}
    </div>
  );
}
